import { GamePlayScene } from '../scenes/GamePlayScene';
import { AudioManager, SoundEffect } from '../systems/AudioManager';
import { t } from '../i18n/lang';

export interface ActiveBoost {
  type: string;
  endTime: number;
  multiplier: number;
  description: string;
}

export interface AdReward {
  type: 'credits' | 'revive' | 'boost' | 'currency';
  amount?: number;
  duration?: number;
  description: string;
}

export class AdBoostManager {
  private scene: GamePlayScene;
  private audioManager: AudioManager;
  
  // Boost tracking
  private activeBoosts: Map<string, ActiveBoost> = new Map();
  private adCooldowns: Map<string, number> = new Map();
  private dailyAdCount: number = 0;
  private maxDailyAds: number = 10;
  
  // UI elements
  private adPanel: Phaser.GameObjects.Container | null = null;
  private boostIndicators: Phaser.GameObjects.Container | null = null;

  constructor(scene: GamePlayScene) {
    this.scene = scene;
    this.audioManager = AudioManager.getInstance(scene);
    this.loadAdState();
  }

  createAdUI(): void {
    this.createAdPanel();
    this.createBoostIndicators();
  }

  updateActiveBoosts(time: number): void {
    const expiredBoosts: string[] = [];
    
    this.activeBoosts.forEach((boost, type) => {
      if (time >= boost.endTime) {
        expiredBoosts.push(type);
      }
    });
    
    // Remove expired boosts
    expiredBoosts.forEach(type => {
      this.activeBoosts.delete(type);
      this.showBoostExpiredMessage(type);
    });
    
    // Update UI
    this.updateBoostUI();
    this.updateAdPanelAvailability();
  }

  applyAdReward(rewardType: string): AdReward | null {
    if (!this.canWatchAd(rewardType)) return null;
    
    this.dailyAdCount++;
    this.setAdCooldown(rewardType, 30000); // 30 second cooldown
    
    let reward: AdReward;
    
    switch (rewardType) {
      case 'double_credits':
        reward = this.applyDoubleCreditsBoost();
        break;
      case 'tower_damage':
        reward = this.applyTowerDamageBoost();
        break;
      case 'pet_speed':
        reward = this.applyPetSpeedBoost();
        break;
      case 'free_gold':
        reward = this.applyFreeGold();
        break;
      case 'free_ruby':
        reward = this.applyFreeRuby();
        break;
      case 'revive':
        reward = this.applyRevive();
        break;
      case 'equipment':
        reward = this.applyFreeEquipment();
        break;
      default:
        return null;
    }
    
    this.audioManager.playSFX(SoundEffect.AD_REWARD);
    this.showAdRewardNotification(reward);
    this.saveAdState();
    
    return reward;
  }

  watchAdForRevive(): boolean {
    if (!this.canWatchAd('revive')) return false;
    
    // Simulate ad watching
    this.simulateAdWatch(() => {
      const reward = this.applyAdReward('revive');
      if (reward) {
        (this.scene as any).reviveFromAd();
      }
    });
    
    return true;
  }

  watchAdForBoost(boostType: string): boolean {
    if (!this.canWatchAd(boostType)) return false;
    
    this.simulateAdWatch(() => {
      this.applyAdReward(boostType);
    });
    
    return true;
  }

  private applyDoubleCreditsBoost(): AdReward {
    const boost: ActiveBoost = {
      type: 'double_credits',
      endTime: this.scene.time.now + 60000, // 1 minute
      multiplier: 2.0,
      description: '2x Credits'
    };
    
    this.activeBoosts.set('double_credits', boost);
    
    return {
      type: 'boost',
      duration: 60000,
      description: '2x Credits for 1 minute'
    };
  }

  private applyTowerDamageBoost(): AdReward {
    const boost: ActiveBoost = {
      type: 'tower_damage',
      endTime: this.scene.time.now + 120000, // 2 minutes
      multiplier: 1.5,
      description: '+50% Tower Damage'
    };
    
    this.activeBoosts.set('tower_damage', boost);
    
    return {
      type: 'boost',
      duration: 120000,
      description: '+50% Tower Damage for 2 minutes'
    };
  }

  private applyPetSpeedBoost(): AdReward {
    const boost: ActiveBoost = {
      type: 'pet_speed',
      endTime: this.scene.time.now + 90000, // 1.5 minutes
      multiplier: 2.0,
      description: '2x Pet Speed'
    };
    
    this.activeBoosts.set('pet_speed', boost);
    
    return {
      type: 'boost',
      duration: 90000,
      description: '2x Pet Speed for 1.5 minutes'
    };
  }

  private applyFreeGold(): AdReward {
    const goldAmount = 500 + Math.floor(Math.random() * 300);
    (this.scene as any).addGold(goldAmount);
    
    return {
      type: 'currency',
      amount: goldAmount,
      description: `+${goldAmount} Gold`
    };
  }

  private applyFreeRuby(): AdReward {
    const rubyAmount = 5 + Math.floor(Math.random() * 10);
    (this.scene as any).addRuby(rubyAmount);
    
    return {
      type: 'currency',
      amount: rubyAmount,
      description: `+${rubyAmount} Ruby`
    };
  }

  private applyRevive(): AdReward {
    // This will be handled by the scene
    return {
      type: 'revive',
      description: 'Revived with full health'
    };
  }

  private applyFreeEquipment(): AdReward {
    // Simulate getting random equipment
    const equipmentTypes = ['Common', 'Rare', 'Epic'];
    const rarity = equipmentTypes[Math.floor(Math.random() * equipmentTypes.length)];
    
    // This would integrate with equipment system
    (this.scene as any).addRandomEquipment(rarity);
    
    return {
      type: 'currency',
      description: `Random ${rarity} Equipment`
    };
  }

  private simulateAdWatch(onComplete: () => void): void {
    // Simulate ad loading and watching
    const loadingText = this.scene.add.text(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY,
      'Loading Ad...',
      {
        fontSize: '24px',
        color: '#ffffff',
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: { x: 20, y: 10 },
        fontFamily: 'Arial'
      }
    ).setOrigin(0.5);
    
    // Simulate 2-5 second ad duration
    const adDuration = 2000 + Math.random() * 3000;
    
    this.scene.time.delayedCall(adDuration, () => {
      loadingText.destroy();
      onComplete();
    });
  }

  private createAdPanel(): void {
    this.adPanel = this.scene.add.container(this.scene.cameras.main.width - 200, 300);
    
    // Background
    const bg = this.scene.add.rectangle(0, 0, 180, 200, 0x333366, 0.9);
    this.adPanel.add(bg);
    
    // Title
    const title = this.scene.add.text(-80, -80, 'AD Boosts', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    });
    this.adPanel.add(title);
    
    // Boost buttons
    const boostTypes = [
      { key: 'double_credits', label: '2x Credits', cost: '1 Ad' },
      { key: 'tower_damage', label: '+50% Damage', cost: '1 Ad' },
      { key: 'pet_speed', label: '2x Pet Speed', cost: '1 Ad' },
      { key: 'free_gold', label: 'Free Gold', cost: '1 Ad' }
    ];
    
    boostTypes.forEach((boost, index) => {
      const button = this.scene.add.text(-80, -50 + index * 35, boost.label, {
        fontSize: '13px',
        color: '#ffffff',
        backgroundColor: this.canWatchAd(boost.key) ? '#4CAF50' : '#666666',
        padding: { x: 8, y: 4 },
        fontFamily: 'Arial'
      }).setInteractive({ useHandCursor: true });
      
      button.on('pointerdown', () => {
        if (this.canWatchAd(boost.key)) {
          this.watchAdForBoost(boost.key);
        }
      });
      
      this.adPanel?.add(button);
    });
    
    // Daily limit indicator
    const limitText = this.scene.add.text(-80, 70, `Daily: ${this.dailyAdCount}/${this.maxDailyAds}`, {
      fontSize: '12px',
      color: '#cccccc',
      fontFamily: 'Arial'
    });
    this.adPanel.add(limitText);
  }

  private createBoostIndicators(): void {
    this.boostIndicators = this.scene.add.container(20, this.scene.cameras.main.height - 150);
    
    // Title
    const title = this.scene.add.text(0, 0, 'Active Boosts:', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    });
    this.boostIndicators.add(title);
  }

  private updateBoostUI(): void {
    if (!this.boostIndicators) return;
    
    // Clear existing boost indicators except title
    const children = this.boostIndicators.list.slice(1);
    children.forEach(child => child.destroy());
    
    // Add current boosts
    let yOffset = 25;
    this.activeBoosts.forEach((boost, type) => {
      const timeLeft = Math.max(0, boost.endTime - this.scene.time.now);
      const timeLeftSeconds = Math.ceil(timeLeft / 1000);
      
      const boostText = this.scene.add.text(0, yOffset, 
        `${boost.description}: ${timeLeftSeconds}s`, {
        fontSize: '12px',
        color: '#00ff00',
        backgroundColor: 'rgba(0,0,0,0.7)',
        padding: { x: 6, y: 3 },
        fontFamily: 'Arial'
      });
      
      this.boostIndicators?.add(boostText);
      yOffset += 25;
    });
  }

  private updateAdPanelAvailability(): void {
    // This would update button states in the ad panel
    // Implementation depends on how buttons are stored
  }

  private canWatchAd(adType: string): boolean {
    // Check daily limit
    if (this.dailyAdCount >= this.maxDailyAds) return false;
    
    // Check cooldown
    const cooldownEnd = this.adCooldowns.get(adType) || 0;
    if (this.scene.time.now < cooldownEnd) return false;
    
    // Check if boost is already active
    if (this.activeBoosts.has(adType)) return false;
    
    return true;
  }

  private setAdCooldown(adType: string, duration: number): void {
    this.adCooldowns.set(adType, this.scene.time.now + duration);
  }

  private showAdRewardNotification(reward: AdReward): void {
    const notification = this.scene.add.text(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY - 150,
      `Reward: ${reward.description}`,
      {
        fontSize: '18px',
        color: '#00ff00',
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: { x: 12, y: 8 },
        fontFamily: 'Arial'
      }
    ).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: notification,
      y: notification.y - 30,
      alpha: 0,
      duration: 2000,
      ease: 'Power2',
      onComplete: () => notification.destroy()
    });
  }

  private showBoostExpiredMessage(boostType: string): void {
    const message = this.scene.add.text(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY + 100,
      `${boostType} boost expired`,
      {
        fontSize: '16px',
        color: '#ffaa00',
        backgroundColor: 'rgba(0,0,0,0.7)',
        padding: { x: 10, y: 6 },
        fontFamily: 'Arial'
      }
    ).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: message,
      alpha: 0,
      duration: 1500,
      ease: 'Power2',
      onComplete: () => message.destroy()
    });
  }

  private loadAdState(): void {
    const saved = localStorage.getItem('earthDefense_adState');
    if (saved) {
      const state = JSON.parse(saved);
      this.dailyAdCount = state.dailyAdCount || 0;
      
      // Check if it's a new day
      const lastDate = state.lastDate || '';
      const today = new Date().toDateString();
      if (lastDate !== today) {
        this.dailyAdCount = 0;
      }
    }
  }

  private saveAdState(): void {
    const state = {
      dailyAdCount: this.dailyAdCount,
      lastDate: new Date().toDateString()
    };
    localStorage.setItem('earthDefense_adState', JSON.stringify(state));
  }

  // Public getters
  getActiveBoosts(): Map<string, ActiveBoost> {
    return new Map(this.activeBoosts);
  }

  hasActiveBoost(boostType: string): boolean {
    return this.activeBoosts.has(boostType);
  }

  getBoostMultiplier(boostType: string): number {
    const boost = this.activeBoosts.get(boostType);
    return boost ? boost.multiplier : 1.0;
  }

  getDailyAdCount(): number {
    return this.dailyAdCount;
  }

  getRemainingAds(): number {
    return Math.max(0, this.maxDailyAds - this.dailyAdCount);
  }
} 